import { useState, type FormEvent } from "react";
import { Box, Button, Flex, Text, TextField, TextArea } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";
import { PaperPlaneIcon, CheckCircledIcon } from "@radix-ui/react-icons";

type FormStatus = "idle" | "sending" | "success" | "error";

export function ContactForm() {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<FormStatus>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMessage("");

    try {
      const response = await fetch("/api/send-contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name, email, message }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to send message");
      }

      setStatus("success");
      setName("");
      setEmail("");
      setMessage("");
    } catch (error) {
      setStatus("error");
      setErrorMessage(
        error instanceof Error
          ? error.message
          : t("onePager.contact.errorMessage"),
      );
    }
  };

  const isSubmitting = status === "sending";

  if (status === "success") {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        gap="3"
        py="6"
        style={{
          backgroundColor: "#f0fdf4",
          borderRadius: "8px",
          border: "1px solid #86efac",
        }}
      >
        <CheckCircledIcon width="48" height="48" color="#22c55e" />
        <Text size="4" weight="medium" style={{ color: "#166534" }}>
          {t("onePager.contact.successMessage")}
        </Text>
        <Button
          variant="ghost"
          onClick={() => setStatus("idle")}
          style={{ marginTop: "0.5rem" }}
        >
          {t("onePager.contact.sendAnother")}
        </Button>
      </Flex>
    );
  }

  return (
    <Box asChild>
      <form onSubmit={handleSubmit}>
        <Flex direction="column" gap="4">
          <Box>
            <Text as="label" size="2" weight="medium" mb="1">
              {t("onePager.contact.nameLabel")} *
            </Text>
            <TextField.Root
              size="3"
              placeholder={t("onePager.contact.namePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={isSubmitting}
            />
          </Box>

          <Box>
            <Text as="label" size="2" weight="medium" mb="1">
              {t("onePager.contact.emailLabel")} *
            </Text>
            <TextField.Root
              size="3"
              type="email"
              placeholder={t("onePager.contact.emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isSubmitting}
            />
          </Box>

          <Box>
            <Text as="label" size="2" weight="medium" mb="1">
              {t("onePager.contact.messageLabel")} *
            </Text>
            <TextArea
              size="3"
              placeholder={t("onePager.contact.messagePlaceholder")}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              disabled={isSubmitting}
              style={{ minHeight: "120px" }}
            />
          </Box>

          {status === "error" && (
            <Text size="2" color="red">
              {errorMessage || t("onePager.contact.errorMessage")}
            </Text>
          )}

          <Button
            type="submit"
            size="3"
            disabled={isSubmitting}
            style={{ alignSelf: "flex-start" }}
          >
            {isSubmitting ? (
              <Flex align="center" gap="2">
                <Box
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid transparent",
                    borderTopColor: "currentColor",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
                {t("onePager.contact.sending")}
              </Flex>
            ) : (
              <Flex align="center" gap="2">
                <PaperPlaneIcon />
                {t("onePager.contact.sendButton")}
              </Flex>
            )}
          </Button>
        </Flex>
      </form>
    </Box>
  );
}
